import {Errors, KAError} from "@khan/logging";

import type {OfficialClarification} from "#clarifications-package/types.ts";

import type {
    ChangeablePostType,
    ContentKind,
    DiscussionContext,
    FeedbackType,
    FlagType,
    Post,
    PostKey,
    ServerPost,
    SortByType,
    TabsByType,
} from "../types.ts";
import {getPostType, isPostUnanswered} from "../util.tsx";
import {toGqlFeedbackType} from "../util/feedback-type-converters.ts";

import type {VoteType} from "./api.ts";

export type State = {
    // Identifier for the current topic slug
    topicSlug: string;
    // Type of content to which the discussion is attached, e.g. topic, video, etc.
    contentKind: ContentKind;
    // Identifier for content, e.g. video slug.
    contentId: string;
    // The id of the youtube video for the video if this is a video page.
    // Will be undefined on all non-video pages.
    youtubeId: string | null | undefined;
    // Array of discussion posts.
    posts: Record<string, Post>;
    // Array of all the accepted clarifications available.
    // This array will always be empty on non-video pages.
    acceptedClarificationPosts: Array<OfficialClarification>;
    // Mapping from feedback types to tab data.
    tabs: TabsByType;
    // Id of a post to automatically focus on when it becomes available.
    // Usually the id of a newly-submitted post, or the first post that appears after "load more".
    newPostKey: string | null | undefined;
    // Whether the top level input field should take focus.
    focusOnTopLevelInput: boolean;
    // Active tab.
    currentTab: FeedbackType;
    // Active post sort order.
    currentSort: SortByType;
    currentCursor: string;
    qaExpandKey: string | null | undefined;
    // Identifies the tab that the expanded post is found on.
    expandFeedbackType: string | null | undefined;
    // Identifies the currently-expanded input field in a DiscussionList.
    displayInputPostKey: string | null | undefined;
    // The version of the API to be using
    apiId?: string;
    // Key of the clarification that the moderator is currently accepting.
    clarificationBeingAcceptedId: string | null | undefined;
    // Key of the post that the moderator or post author is currently editing.
    postBeingEditedKey: string | null | undefined;
    // If the content item is a project, this property tracks whether there are
    // any help requests that don't have answers.
    // This will be null on non-projecthelp tabs and undefined during the projecthelp
    // posts fetch.
    hasUnansweredHelpRequests?: boolean | null | undefined;
    fetchingPostsStatus: "loading" | "loaded" | "failed";
    fetchingAnswersStatus: Record<string, "loading" | "loaded" | "failed">;
    fetchingRepliesStatus: Record<string, RepliesPageStatus>;
    // Posts that have been selected with a checkbox.
    // Used on the moderation Flag Queue page
    selectedPosts: Record<string, boolean>;
    // clearedPosts is used in the Flag Queue to help keep track of which posts
    // have been updated in the queue (deletePost, clearFlags, changeType). This is
    // so that these posts are not caught by any subsequent "Select All" Bulk Mod Actions
    // while ensuring that we dont affect how the moderation actions work elsewhere
    clearedPosts: Array<string>;
};

type GetDefaultStateParams = Readonly<{
    isLoggedIn: boolean;
}>;

export function getDefaultState(
    loginState: GetDefaultStateParams = {isLoggedIn: false},
    discussionContext?: DiscussionContext,
): State {
    const currentSort = "top-voted";
    return {
        topicSlug: "",
        contentKind: "",
        contentId: "",
        youtubeId: null,
        posts: {},
        acceptedClarificationPosts: [],
        tabs: {
            questions: {
                cursor: "",
                isComplete: true,
                postKeys: [],
                totalCount: 0,
            },
            answers: {
                cursor: "",
                isComplete: true,
                postKeys: [],
                totalCount: 0,
            },
            comments: {
                cursor: "",
                isComplete: true,
                postKeys: [],
                totalCount: 0,
            },
            clarifications: {
                cursor: "",
                isComplete: true,
                postKeys: [],
                totalCount: 0,
            },
            projecthelp: {
                cursor: "",
                isComplete: true,
                postKeys: [],
                totalCount: 0,
            },
        },
        newPostKey: null,
        focusOnTopLevelInput: false,
        currentTab: discussionContext?.showProjectFeedback
            ? "projecthelp"
            : "questions",
        currentSort,
        currentCursor: "",
        qaExpandKey: null,
        expandFeedbackType: null,
        displayInputPostKey: null,
        apiId: "",
        clarificationBeingAcceptedId: null,
        postBeingEditedKey: null,
        hasUnansweredHelpRequests: null,
        selectedPosts: {},
        clearedPosts: [],
        fetchingPostsStatus: "loaded",
        fetchingAnswersStatus: {},
        fetchingRepliesStatus: {},
    };
}

export type Action =
    | {
          type: "initializeContent";
          topicSlug: string;
          contentKind: ContentKind;
          contentId: string;
          youtubeId: string | null | undefined;
          qaExpandKey?: string;
          expandFeedbackType?: FeedbackType;
          hasUnansweredHelpRequests?: boolean | null | undefined;
      }
    | {
          type: "initializeDiscussionListComponent";
          tab: FeedbackType;
          topicSlug: string;
          cursor: string;
          apiId: string | null | undefined;
      }
    | {
          type: "postSuccess";
          post: Post;
          tab: FeedbackType;
      }
    | {
          type: "updatePost";
          post: Post;
      }
    | {
          type: "fetchingPosts";
      }
    | {
          type: "fetchingAnswers";
          parentKey: string;
          status: "loading" | "loaded" | "failed";
      }
    | {
          type: "fetchingReplies";
          parentKey: string;
          status: RepliesPageStatus;
      }
    | {
          type: "clearLoadingStatus";
      }
    | {
          type: "updatePosts" | "appendPosts";
          tab: FeedbackType;
          posts: Array<ServerPost>;
          cursor: string | null | undefined;
          isComplete: boolean;
          totalCount: number;
          acceptedClarificationPosts?: Array<OfficialClarification>;
          youtubeId?: string;
          hasUnansweredHelpRequests?: boolean | null | undefined;
      }
    | {
          type: "upvotePost";
          postKey: string;
      }
    | {
          type: "downvotePost";
          postKey: string;
      }
    | {
          type: "flagPost";
          postKey: string;
          flag: FlagType;
      }
    | {
          type: "changeTab";
          tab: FeedbackType;
      }
    | {
          type: "changeSortBy";
          sort: SortByType;
      }
    | {
          type: "resetNewPostKey";
      }
    | {
          type: "setFocusOnTopLevelInput";
      }
    | {
          type: "resetFocusOnTopLevelInput";
      }
    | {
          type: "resetExpandKey";
      }
    | {
          type: "changePostType";
          postKey: string;
          postType: ChangeablePostType;
      }
    | {
          type: "clearFlags";
          postKey: string;
      }
    | {
          type: "updateReplies";
          parentKey: string;
          replies: Array<ServerPost>;
      }
    | {
          type: "updateRepliesPaginated";
          parentKey: string;
          replies: Array<ServerPost>;
          repliesCursor: string | null | undefined;
      }
    | {
          type: "updateAnswers";
          parentKey: string;
          answers: Array<ServerPost>;
      }
    | {
          type: "addReply";
          parentKey: string;
          post: Post;
      }
    | {
          type: "deletePost";
          postKey: string;
      }
    | {
          type: "deleteAcceptedClarificationPost";
          clarificationId: string;
      }
    | {
          type: "acceptClarification";
          originalClarificationId: string;
          clarification: OfficialClarification;
      }
    | {
          type: "startAcceptingClarification";
          clarificationId: string;
      }
    | {
          type: "stopAcceptingClarification";
      }
    | {
          type: "startEditingPost";
          postKey: PostKey;
      }
    | {
          type: "stopEditingPost";
      }
    | {
          type: "editAcceptedClarification";
          clarification: OfficialClarification;
      }
    | {
          type: "undeletePost";
          postKey: string;
      }
    | {
          type: "lockThread";
          postKey: string;
      }
    | {
          type: "unlockThread";
          postKey: string;
      }
    | {
          type: "pinThread";
          postKey: string;
      }
    | {
          type: "unpinThread";
          postKey: string;
      }
    | {
          type: "editPost";
          postKey: string;
          content: string;
      }
    | {
          type: "toggleReplyInput";
          postKey: string;
      }
    | {
          type: "selectPosts";
          postKeys: Array<string>;
      }
    | {
          type: "skipPosts";
          postKeys: Array<string>;
      }
    | {
          type: "deselectPost";
          postKey: string;
      }
    | {
          type: "deselectAll";
      }
    | {
          type: "selectErroredPosts";
          postKeys: Array<string>;
      }
    | {
          type: "hideProgramFromHotlist";
          postKey: string;
          hidden: boolean;
      }
    | {
          type: "fetchingPostsFailed";
      };

const UPVOTE = 1;
const DOWNVOTE = -1;

export function calcSumVotes(post: Post, voteType: VoteType): number {
    if (voteType === UPVOTE) {
        if (post.upVoted) {
            return post.sumVotesIncremented - 1;
        }
        return post.sumVotesIncremented + (post.downVoted ? 2 : 1);
    }
    if (post.downVoted) {
        return post.sumVotesIncremented + 1;
    }
    return post.sumVotesIncremented - (post.upVoted ? 2 : 1);
}

export function flattenPosts(posts: Array<ServerPost>): Record<string, Post> {
    return posts.reduce<Record<string, any>>((flattenedPosts, post) => {
        const answers = post.answers || [];
        return {
            ...flattenedPosts,
            ...flattenPosts(answers),
            [post.key]: {
                ...post,
                answers: answers.map((answer) => answer.key),
                replies: [],
            },
        };
    }, {});
}

/**
 * Merges an existing discussions Redux store state with new posts data for a specific tab
 *
 * @param {array} newPosts The new list of posts to include with the given tab
 * @param {string} tab The tab to be updated
 * @param {boolean} isComplete Whether there are more posts to fetch
 * @param {number} totalCount The total count of posts
 * @param {string} [cursor] The next cursor for pagination
 * @param {array} [acceptedClarificationPosts] The accepted clarification posts to include with the given tab
 * @returns {object} The new discussions Redux store state
 */
export function mergeStateWithNewPosts(
    state: State,
    newPosts: Array<ServerPost>,
    tab: FeedbackType,
    isComplete: boolean,
    totalCount: number,
    cursor?: string | null,
    acceptedClarificationPosts?: Array<OfficialClarification> | null,
): State {
    const {tabs} = state;

    const combinedPosts = {
        ...state.posts,
        ...flattenPosts(newPosts),
    } as const;
    const postKeys = newPosts.map((post) => post.key);

    const getHasUnansweredHelpRequests = (posts: Record<string, Post>) => {
        const findUnansweredPosts = (post: ServerPost): boolean => {
            // We only want to know if the top level posts in the
            // "Help Requests" tab has valid answers.
            if (
                //TODO(LP-11774) - just use feedbackType
                post.type === "projecthelp" ||
                post.feedbackType === "PROJECT_HELP_QUESTION"
            ) {
                return isPostUnanswered(post.key, posts);
            }
            return false;
        };

        return tab === "projecthelp"
            ? newPosts.some(findUnansweredPosts)
            : state.hasUnansweredHelpRequests;
    };

    return {
        ...state,
        fetchingPostsStatus: "loaded",
        posts: combinedPosts,
        acceptedClarificationPosts: acceptedClarificationPosts ?? [],
        hasUnansweredHelpRequests: getHasUnansweredHelpRequests(combinedPosts),
        tabs: {
            ...tabs,
            [tab as string]: {
                postKeys,
                cursor,
                isComplete,
                totalCount,
            },
        },
    };
}

export default function discussionReducer(
    state: State = getDefaultState(),
    action: Action,
): State {
    switch (action.type) {
        case "initializeContent": {
            const {topicSlug, contentKind, contentId, youtubeId, qaExpandKey} =
                action;

            const expandFeedbackType =
                // @ts-expect-error - TS2367 - This condition will always return 'false' since the types 'FeedbackType | undefined' and '"projectfeedback"' have no overlap.
                action.expandFeedbackType === "projectfeedback"
                    ? "projecthelp"
                    : action.expandFeedbackType;

            return {
                ...state,
                topicSlug,
                contentKind,
                contentId,
                youtubeId,
                qaExpandKey,
                expandFeedbackType,
                currentTab: expandFeedbackType
                    ? expandFeedbackType
                    : state.currentTab,
            };
        }

        case "initializeDiscussionListComponent": {
            const {topicSlug, tab, cursor, apiId} = action;
            const {tabs} = state;

            return {
                ...state,
                topicSlug: topicSlug,
                apiId: apiId || "",
                contentKind: "Topic",
                currentTab: tab,
                tabs: {
                    ...tabs,
                    [tab as string]: {
                        ...tabs[tab],
                        cursor,
                    },
                },
            };
        }

        /**
         * Receive a newly created post by the current user and pass it down to the
         * DiscussionListTabs to update the DiscussionList.
         */
        case "postSuccess": {
            const {post, tab} = action;
            // ensure that answers is always defined
            post.answers = post.answers || [];
            // ensure that replies is always defined
            post.replies = post.replies || [];
            let tabPostsKeysForType: Array<string> = [];
            let posts: Record<string, any> = {};

            const {questionKey: parentKey} = post;
            const isChildType = post.type === "answer" || post.type === "reply";
            if (parentKey && isChildType) {
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                tabPostsKeysForType = [...state.tabs[tab].postKeys];
                const parentQuestion = {...state.posts[parentKey]} as const;

                // Answers are sorted newest to oldest, while replies are sorted oldest to newest.
                // Therefore, we need to unshift answers (to first index) and push replies (to last index).
                switch (post.type) {
                    case "answer":
                        parentQuestion.answers.unshift(post.key);
                        // @ts-expect-error - TS2540 - Cannot assign to 'answerCount' because it is a read-only property.
                        parentQuestion.answerCount =
                            (parentQuestion.answerCount ?? 0) + 1;
                        break;
                    case "reply":
                        parentQuestion.replies.push(post.key);
                        // @ts-expect-error - TS2540 - Cannot assign to 'replyCount' because it is a read-only property.
                        parentQuestion.replyCount =
                            (parentQuestion.replyCount ?? 0) + 1;
                        break;
                }

                posts = {
                    ...state.posts,
                    [parentKey]: parentQuestion,
                    [post.key]: post,
                };
            } else {
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                tabPostsKeysForType = [post.key, ...state.tabs[tab].postKeys];
                posts = {
                    [post.key]: post,
                    ...state.posts,
                };
            }

            let hasUnansweredHelpRequests = state.hasUnansweredHelpRequests;
            if (post.type === "projecthelp") {
                // If a user is posting a help request, we know for sure that there are
                // unanswered help requests because they literally just posted it.
                hasUnansweredHelpRequests = true;
            } else if (
                tab === "projecthelp" &&
                post.type === "answer" &&
                hasUnansweredHelpRequests
            ) {
                // If this project had unanswered help requests and the user posted an answer
                // on a help request, this project may now have no unanswered help requests.
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                const projecthelpPostKeys = state.tabs.projecthelp.postKeys;
                if (projecthelpPostKeys.length === 1) {
                    // If there's only one help request, and it was just answered,
                    // we know for sure there are no unanswered help requests.
                    hasUnansweredHelpRequests = false;
                } else {
                    const unansweredHelpRequest = projecthelpPostKeys.find(
                        (postKey) => isPostUnanswered(postKey, posts),
                    );
                    hasUnansweredHelpRequests = !!unansweredHelpRequest;
                }
            }

            return {
                ...state,
                posts: posts,
                hasUnansweredHelpRequests: hasUnansweredHelpRequests,
                tabs: {
                    ...state.tabs,
                    [tab as string]: {
                        ...state.tabs[tab],
                        postKeys: tabPostsKeysForType,
                        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                        totalCount: state.tabs[tab].totalCount + 1,
                    },
                },
                newPostKey: post.key,
            };
        }

        case "fetchingPosts": {
            return {
                ...state,
                fetchingPostsStatus: "loading",
            };
        }

        /**
         * Load posts from the server.
         */
        case "updatePosts": {
            return mergeStateWithNewPosts(
                state,
                action.posts,
                action.tab,
                action.isComplete,
                action.totalCount,
                action.cursor,
                action.acceptedClarificationPosts,
            );
        }

        case "appendPosts": {
            const {tab, cursor, isComplete} = action;
            const {tabs} = state;

            // Filtering out duplicate posts in case the same action is
            // called multiple times (e.g. rapidly clicking "Show more...")
            const filteredPosts = action.posts.filter(
                (post) => !Object.keys(state.posts).includes(post.key),
            );
            const posts = {
                ...state.posts,
                ...flattenPosts(filteredPosts),
            } as const;

            // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            const existingPostKeys = tabs[tab].postKeys;
            const newPostKeys = filteredPosts.map((post) => post.key);

            const postKeys = [...existingPostKeys, ...newPostKeys];

            return {
                ...state,
                fetchingPostsStatus: "loaded",
                posts,
                newPostKey: newPostKeys.length ? newPostKeys[0] : null,
                tabs: {
                    ...tabs,
                    [tab as string]: {
                        ...state.tabs[tab],
                        postKeys,
                        cursor: cursor,
                        isComplete: isComplete,
                    },
                },
            };
        }

        case "updatePost": {
            const {post} = action;

            const oldPost = state.posts[post.key];

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [post.key]: {
                        ...oldPost,
                        ...post,
                    },
                },
            };
        }

        case "updateReplies": {
            const {replies, parentKey} = action;

            const posts = {
                ...state.posts,
                ...flattenPosts(replies),
            } as const;

            return {
                ...state,
                posts: {
                    ...posts,
                    [parentKey]: {
                        ...posts[parentKey],
                        replies: replies.map((reply) => reply.key),
                    },
                },
            };
        }

        case "updateRepliesPaginated": {
            const {replies, parentKey, repliesCursor} = action;

            const posts = {
                ...state.posts,
                ...flattenPosts(replies),
            } as const;

            // Merge new replies with exisitng replies while avoiding duplicates
            const currentReplies = posts[parentKey].replies || [];
            const newReplies = [...currentReplies];
            replies.forEach((reply) => {
                if (currentReplies.indexOf(reply.key) === -1) {
                    newReplies.push(reply.key);
                }
            });

            return {
                ...state,
                posts: {
                    ...posts,
                    [parentKey]: {
                        ...posts[parentKey],
                        replies: newReplies,
                        repliesCursor,
                    },
                },
            };
        }

        case "updateAnswers": {
            const {answers, parentKey} = action;

            const posts = {
                ...state.posts,
                ...flattenPosts(answers),
            } as const;

            return {
                ...state,
                posts: {
                    ...posts,
                    [parentKey]: {
                        ...posts[parentKey],
                        answers: answers.map((answer) => answer.key),
                    },
                },
            };
        }

        case "fetchingAnswers": {
            const {parentKey, status} = action;

            return {
                ...state,
                fetchingAnswersStatus: {
                    ...state.fetchingAnswersStatus,
                    [parentKey]: status,
                },
            };
        }

        case "fetchingReplies": {
            const {parentKey, status} = action;

            return {
                ...state,
                fetchingRepliesStatus: {
                    ...state.fetchingRepliesStatus,
                    [parentKey]: status,
                },
            };
        }

        case "clearLoadingStatus": {
            return {
                ...state,
                fetchingAnswersStatus: {},
                fetchingRepliesStatus: {},
            };
        }

        case "addReply": {
            const {parentKey, post} = action;

            const posts = {
                ...state.posts,
                [post.key]: post,
            } as const;
            posts[parentKey].replyCount = posts[parentKey].replyCount ?? 0;
            posts[parentKey].replyCount += 1;
            const existingReplies = posts[parentKey].replies || [];
            posts[parentKey].replies = [...existingReplies, post.key];

            return {
                ...state,
                posts,
                newPostKey: post.key,
            };
        }

        case "upvotePost": {
            const {postKey} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [postKey]: {
                        ...post,
                        downVoted: false,
                        upVoted: !post.upVoted,
                        sumVotesIncremented: calcSumVotes(post, UPVOTE),
                    },
                },
            };
        }

        case "changePostType": {
            const {postKey, postType: resultingPostType} = action;

            const post = state.posts[postKey];

            const fromType = getPostType(post);

            if (resultingPostType === "answer") {
                throw new KAError(
                    "Cannot change the type to an Answer",
                    Errors.Internal,
                );
            } else if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            } else if (!fromType) {
                throw new KAError("post has no type", Errors.Internal);
            } else if (
                fromType === "reply" ||
                fromType === "scratchpad" ||
                fromType === "projecthelp" ||
                fromType === "projecteval" ||
                fromType === "projectevalanswer" ||
                fromType === "projecthelpanswer"
            ) {
                throw new KAError(
                    `posts of type "${fromType}" can't be changed to another type`,
                    Errors.Internal,
                );
            }

            const tab = {
                comment: "comments",
                question: "questions",
                clarification: "clarifications",
            }[resultingPostType];
            const tabPostKeys = state.tabs[tab].postKeys;

            // @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"clarification" | "question" | "comment"' and '"projecthelp"' have no overlap.
            if (fromType === "answer" || fromType === "projecthelp") {
                if (!post.questionKey) {
                    throw new KAError("answer has no parent", Errors.Internal);
                }

                const parentPost = state.posts[post.questionKey];

                // If parentPost is not found, this only means that it is not
                // in the Redux store at this time, so there is no need to
                // update the parent post's answers array.
                let postsState = state.posts;
                if (parentPost) {
                    postsState = {
                        ...state.posts,
                        [parentPost.key]: {
                            ...parentPost,
                            // remove the post from the set of answer since it's
                            // no longer an answer
                            answers: parentPost.answers.filter(
                                (key) => key !== post.key,
                            ),
                        },
                    };
                }

                return {
                    ...state,
                    posts: {
                        ...postsState,
                        [post.key]: {
                            ...post,
                            // update the post's type to the new type
                            type: resultingPostType,
                            feedbackType: toGqlFeedbackType(resultingPostType),
                        },
                    },
                    tabs: {
                        ...state.tabs,
                        [tab as string]: {
                            ...state.tabs[tab],
                            postKeys: [post.key, ...tabPostKeys],
                            totalCount: state.tabs[tab].totalCount + 1,
                        },
                    },
                    clearedPosts: [...state.clearedPosts, post.key],
                };
            }
            const oldTabType = {
                comment: "comments",
                question: "questions",
                clarification: "clarifications",
            }[fromType];

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [post.key]: {
                        ...post,
                        // update the post's type to the new type
                        type: resultingPostType,
                        feedbackType: toGqlFeedbackType(resultingPostType),
                    },
                },
                tabs: {
                    ...state.tabs,
                    // remove the post from the tab that it was previously
                    // in
                    [oldTabType as string]: {
                        ...state.tabs[oldTabType],
                        postKeys: state.tabs[oldTabType].postKeys.filter(
                            (key) => key !== post.key,
                        ),
                        totalCount: state.tabs[oldTabType].totalCount - 1,
                    },
                    [tab as string]: {
                        ...state.tabs[tab],
                        postKeys: [post.key, ...tabPostKeys],
                        totalCount: state.tabs[tab].totalCount + 1,
                    },
                },
                clearedPosts: [...state.clearedPosts, post.key],
            };
        }

        case "clearFlags": {
            const {postKey} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [postKey]: {
                        ...post,
                        flags: [],
                        flaggedByUser: false,
                    },
                },
                // Ensure the post we cleared flags for is no longer selected.
                selectedPosts: {...state.selectedPosts, [postKey]: false},

                // Keep a record of what posts were cleared from the mod queue
                clearedPosts: [...state.clearedPosts, post.key],
            };
        }

        case "downvotePost": {
            const {postKey} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [postKey]: {
                        ...post,
                        downVoted: !post.downVoted,
                        upVoted: false,
                        sumVotesIncremented: calcSumVotes(post, DOWNVOTE),
                    },
                },
            };
        }

        case "flagPost": {
            const {postKey, flag} = action;
            const post = state.posts[postKey];
            const flags = post.flags || [];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [postKey]: {
                        ...post,
                        flaggedByUser: true,
                        flags: [...flags, flag],
                    },
                },
            };
        }

        case "changeTab": {
            return {
                ...state,
                currentTab: action.tab,
                // We reset the newPostKey to avoid auto-focusing on a post the user
                // posted on this tab previously.
                newPostKey: null,
            };
        }

        case "changeSortBy": {
            return {
                ...state,
                currentSort: action.sort,
                // We reset the newPostKey to avoid auto-focusing on a post the user
                // posted on this tab previously.
                newPostKey: null,
            };
        }

        /**
         * After the DiscussionListTabs have received the information on the newly
         * created post, it calls this method to reset the key info in state here so
         * we know it's no longer needed.
         */
        case "resetNewPostKey":
            return {
                ...state,
                newPostKey: null,
            };

        /**
         * We sometimes need to force the top level input field (the one at the top)
         * of the discussions feature) to take focus in order to make it easier
         * for a user to submit a new post.
         */
        case "setFocusOnTopLevelInput":
            return {
                ...state,
                focusOnTopLevelInput: true,
            };

        /**
         * Once the top level input has taken focus, we reset that value so we can
         * tell it to take focus again later if needed.
         */
        case "resetFocusOnTopLevelInput":
            return {
                ...state,
                focusOnTopLevelInput: false,
            };

        /**
         * After the DiscussionPost has received the information on the post
         * to expand, it calls this method to reset the key in state so we know
         * it's no longer needed.
         */
        case "resetExpandKey":
            return {
                ...state,
                expandFeedbackType: null,
                qaExpandKey: null,
            };

        /**
         * Delete a post.
         */
        case "deletePost": {
            const {currentTab} = state;
            const {postKey} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            const updatedPostsDict = {
                ...state.posts,
                [postKey]: {
                    ...post,
                    deleted: true,
                },
            } as const;

            const isHelpRequest = post.type === "projecthelp";
            let hasUnansweredHelpRequests = state.hasUnansweredHelpRequests;

            // If there are unanswered help requests, and the user is deleting a help
            // request, the remaining help requests may all be answered.
            if (isHelpRequest && hasUnansweredHelpRequests) {
                hasUnansweredHelpRequests = false;

                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                const projecthelpPostKeys = state.tabs.projecthelp.postKeys;
                // If there's only one help request, and it was just deleted,
                // we know for sure there are no unanswered help requests.
                if (projecthelpPostKeys.length > 1) {
                    hasUnansweredHelpRequests = projecthelpPostKeys.some(
                        (postKey) =>
                            isPostUnanswered(postKey, updatedPostsDict),
                    );
                }
            }

            // If all the help requests have answers, and the user is deleting an
            // answer on a help request, that question may be unanswered now.
            if (
                currentTab === "projecthelp" &&
                post.type === "answer" &&
                !hasUnansweredHelpRequests &&
                post.questionKey
            ) {
                const helpRequestForAnswer = state.posts[post.questionKey];
                const answers = helpRequestForAnswer.answers;

                // Answers that aren't visible to the post author don't count as valid answers.
                const hasVisibleAnswer = answers.some((answerKey) => {
                    const answer = updatedPostsDict[answerKey];
                    return (
                        answer && !answer.deleted && !answer.appearsAsDeleted
                    );
                });
                hasUnansweredHelpRequests = !hasVisibleAnswer;
            }

            return {
                ...state,
                hasUnansweredHelpRequests: hasUnansweredHelpRequests,
                posts: updatedPostsDict,
                clearedPosts: [...state.clearedPosts, post.key],
            };
        }

        /**
         * Delete an accepted clarification post.
         */
        case "deleteAcceptedClarificationPost": {
            const {clarificationId} = action;

            const filteredAcceptedClarifications =
                state.acceptedClarificationPosts.filter(
                    (clarification) => clarification.id !== clarificationId,
                );

            if (
                filteredAcceptedClarifications.length ===
                state.acceptedClarificationPosts.length
            ) {
                throw new KAError(
                    "accepted clarification doesn't exist",
                    Errors.Internal,
                );
            }

            return {
                ...state,
                acceptedClarificationPosts: filteredAcceptedClarifications,
            };
        }

        /**
         * Undelete a post.
         */
        case "undeletePost": {
            const {postKey} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [postKey]: {
                        ...post,
                        deleted: false,
                    },
                },
            };
        }

        case "editPost": {
            const {postKey, content} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [postKey]: {
                        ...post,
                        content,
                    },
                },
            };
        }

        case "lockThread": {
            const {postKey} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            if (post.type !== "question" && post.type !== "comment") {
                throw new KAError(
                    "You can only lock questions and tips&thanks",
                    Errors.Internal,
                );
            }

            return toggleLockOnThread(postKey, state, true);
        }

        case "unlockThread": {
            const {postKey} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            if (post.type !== "question" && post.type !== "comment") {
                throw new KAError(
                    "You can only lock questions and tips&thanks",
                    Errors.Internal,
                );
            }

            return toggleLockOnThread(postKey, state, false);
        }

        case "pinThread": {
            const {postKey} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            if (
                post.type !== "question" &&
                post.type !== "answer" &&
                post.type !== "comment"
            ) {
                throw new KAError(
                    "can only lock questions, answers, or tips&thanks",
                    Errors.Internal,
                );
            }

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [postKey]: {
                        ...post,
                        isPinned: true,
                    },
                },
            };
        }

        case "unpinThread": {
            const {postKey} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            if (
                post.type !== "question" &&
                post.type !== "answer" &&
                post.type !== "comment"
            ) {
                throw new KAError(
                    "can only unlock questions, answers, or tips&thanks",
                    Errors.Internal,
                );
            }

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [postKey]: {
                        ...post,
                        isPinned: false,
                    },
                },
            };
        }
        /**
         * Remove the suggested clarification from the normal post list
         * and add it to the list of accepted clarification posts.
         */
        case "acceptClarification": {
            const {originalClarificationId, clarification} = action;

            const filteredPosts = {...state.posts} as const;
            // @ts-expect-error - TS2542 - Index signature in type '{ readonly [x: string]: Post; }' only permits reading.
            delete filteredPosts[originalClarificationId];
            const filteredClarificationKeys =
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                state.tabs.clarifications.postKeys.filter(
                    (postKeyInList) =>
                        postKeyInList !== originalClarificationId,
                );

            if (
                filteredClarificationKeys.length ===
                // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                state.tabs.clarifications.postKeys.length
            ) {
                throw new KAError(
                    "Couldn't find the clarification to delete.",
                    Errors.Internal,
                );
            }

            return {
                ...state,
                posts: filteredPosts,
                acceptedClarificationPosts: [
                    ...state.acceptedClarificationPosts,
                    clarification,
                ],
                tabs: {
                    ...state.tabs,
                    // @ts-expect-error - TS2322 - Type '{ postKeys: string[]; totalCount: number; cursor?: string | undefined; isComplete?: boolean | undefined; }' is not assignable to type 'Tab'.
                    clarifications: {
                        ...state.tabs.clarifications,
                        postKeys: filteredClarificationKeys,
                        // NOTE(diedra): totalCount is not currently being used, but we plan
                        // to bring it back at some point in the shiny distant future.
                        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
                        totalCount: state.tabs.clarifications.totalCount - 1,
                    },
                },
            };
        }

        /**
         * Track which accepted clarification is currently being edited.
         */
        case "startAcceptingClarification": {
            return {
                ...state,
                clarificationBeingAcceptedId: action.clarificationId,
            };
        }

        /**
         * Stop tracking which accepted clarification is currently being edited.
         */
        case "stopAcceptingClarification": {
            return {
                ...state,
                clarificationBeingAcceptedId: null,
            };
        }

        /**
         * Track which post is currently being edited.
         */
        case "startEditingPost": {
            return {
                ...state,
                postBeingEditedKey: action.postKey,
            };
        }

        /**
         * Stop tracking which post is currently being edited.
         */
        case "stopEditingPost": {
            return {
                ...state,
                postBeingEditedKey: null,
            };
        }

        /**
         * Edit existing accepted clarification.
         */
        case "editAcceptedClarification": {
            const {clarification} = action;

            const updatedAcceptedClarifications =
                state.acceptedClarificationPosts.map(
                    (clarificationInList: OfficialClarification) => {
                        if (clarificationInList.id === clarification.id) {
                            return clarification;
                        }
                        return clarificationInList;
                    },
                );

            return {
                ...state,
                acceptedClarificationPosts: updatedAcceptedClarifications,
            };
        }

        case "toggleReplyInput":
            return {
                ...state,
                displayInputPostKey:
                    state.displayInputPostKey === action.postKey
                        ? null
                        : action.postKey,
            };

        case "selectPosts": {
            const selectedPosts = {
                ...state.selectedPosts,
            } as const;
            // Filter out the posts that have already been individually
            // moderated in the flag queue when selecting all
            const keysToSelect = action.postKeys.filter(
                (item) => !state.clearedPosts.includes(item),
            );
            keysToSelect.forEach((postKey: string) => {
                // @ts-expect-error - TS2542 - Index signature in type '{ readonly [x: string]: boolean; }' only permits reading.
                selectedPosts[postKey] = true;
            });
            return {
                ...state,
                selectedPosts,
            };
        }

        case "skipPosts": {
            const clearedPosts = [...state.clearedPosts, ...action.postKeys];
            return {
                ...state,
                clearedPosts,
            };
        }

        case "deselectPost": {
            const selectedPosts = {
                ...state.selectedPosts,
                ...{[action.postKey]: false},
            } as const;
            return {
                ...state,
                selectedPosts,
            };
        }

        case "deselectAll":
            return {
                ...state,
                selectedPosts: {},
            };

        // This is a unique case in the mod queue where we want to
        // deselect all previous posts and then select errored posts
        // without causing too many state changes.
        case "selectErroredPosts": {
            const selectedPosts: Record<string, boolean> = {};
            action.postKeys.forEach((postKey: string) => {
                selectedPosts[postKey] = true;
            });
            return {
                ...state,
                selectedPosts,
            };
        }

        case "hideProgramFromHotlist": {
            const {postKey, hidden} = action;
            const post = state.posts[postKey];

            if (!post) {
                throw new KAError("post doesn't exist", Errors.Internal);
            }

            return {
                ...state,
                posts: {
                    ...state.posts,
                    [postKey]: {
                        ...post,
                        hideFromHotlist: hidden,
                    },
                },
            };
        }

        case "fetchingPostsFailed":
            return {
                ...state,
                fetchingPostsStatus: "failed",
            };

        default:
            // If switch case is non-exhaustive this will cause a TypeScript error.
            const _: never = action;
            return state;
    }
}

const toggleLockOnThread = (
    threadKey: string,
    state: State,
    lock: boolean,
): State => {
    const topPost = state.posts[threadKey];
    const postsToLock = [threadKey];
    postsToLock.push(...(topPost.answers || []), ...(topPost.replies || []));

    if (topPost.answers) {
        for (const answerKey of topPost.answers) {
            const answer = state.posts[answerKey];
            if (answer) {
                postsToLock.push(...answer.replies);
            }
        }
    }

    const newState = {
        ...state,
    } as const;

    for (const postKey of postsToLock) {
        if (newState.posts[postKey]) {
            newState.posts[postKey] = {
                ...newState.posts[postKey],
                isLocked: lock,
            };
        }
    }

    return newState;
};

export type RepliesPageStatus =
    | "loading_first"
    | "loading_next"
    | "complete"
    | "partial"
    | "failed";
